/**
 * #363 — the site map's numbers, kept out of the component so they can be checked on their own.
 *
 * The drawing is fitted inside the map box by its own width and height (contain, centred): `fitDrawing`. On top of
 * that fit the user zooms (1x to SITE_MAX_ZOOM: the 1600 px drawing is the sharpest DLR have, so no deeper zoom) and
 * pans: a `SiteView` holds the zoom and the pan in box pixels of the drawing's top left corner from its fitted spot.
 * On screen the drawing's top left is at (fit.left + x, fit.top + y) and it is fit.width x fit.height times the zoom.
 * Zoomed in, the drawing always covers the box on each axis where it is larger than the box; an axis where it is
 * still smaller stays centred. Pins are fractions (0..1) of the drawing's width and height, origin top left.
 */
import type { CameraMapPin } from '@/types'
import { cameraNumber } from '@/lib/cameraNumber'

export const SITE_MAX_ZOOM = 3
/** A placed pin stays this far inside the drawing's edges, so its head is never cut off. */
export const PIN_MIN = 0.01
export const PIN_MAX = 0.99

export interface Size { width: number; height: number }
export interface Rect extends Size { left: number; top: number }
export interface Point { x: number; y: number }
export interface SiteView { zoom: number; x: number; y: number }

export const FITTED: SiteView = { zoom: 1, x: 0, y: 0 }

// -0 and +0 read the same on screen, but not in a comparison
const plain = (n: number) => n + 0

/** The drawing fitted inside the box (contain, centred). No size on either side: an empty rect. */
export function fitDrawing(box: Size, drawing: Size): Rect {
  if (!(box.width > 0 && box.height > 0 && drawing.width > 0 && drawing.height > 0)) return { left: 0, top: 0, width: 0, height: 0 }
  const scale = Math.min(box.width / drawing.width, box.height / drawing.height)
  const width = drawing.width * scale
  const height = drawing.height * scale
  return { left: (box.width - width) / 2, top: (box.height - height) / 2, width, height }
}

/** One axis of the pan: centred while the drawing is smaller than the box, else held so it covers the box. */
function clampAxis(pan: number, boxSize: number, fitStart: number, fitSize: number, zoom: number): number {
  const size = fitSize * zoom
  if (size <= boxSize) return plain((fitSize - size) / 2)
  return plain(Math.min(Math.max(pan, boxSize - fitStart - size), -fitStart))
}

/** The view held inside its bounds: zoom 1x..3x, the pan as above. */
export function clampView(view: SiteView, box: Size, fit: Rect): SiteView {
  const zoom = Math.min(Math.max(view.zoom, 1), SITE_MAX_ZOOM)
  return {
    zoom,
    x: clampAxis(view.x, box.width, fit.left, fit.width, zoom),
    y: clampAxis(view.y, box.height, fit.top, fit.height, zoom),
  }
}

/** Zoom by `factor` keeping the drawing's point under `point` (box pixels) where it is, then held in bounds. */
export function zoomAround(view: SiteView, factor: number, point: Point, box: Size, fit: Rect): SiteView {
  const zoom = Math.min(Math.max(view.zoom * factor, 1), SITE_MAX_ZOOM)
  const u = (point.x - fit.left - view.x) / view.zoom
  const v = (point.y - fit.top - view.y) / view.zoom
  return clampView({ zoom, x: point.x - fit.left - u * zoom, y: point.y - fit.top - v * zoom }, box, fit)
}

/** Move the drawing by (dx, dy) box pixels, held in bounds. */
export function panView(view: SiteView, dx: number, dy: number, box: Size, fit: Rect): SiteView {
  return clampView({ ...view, x: view.x + dx, y: view.y + dy }, box, fit)
}

/** A point of the box as fractions of the drawing; outside the drawing it falls outside 0..1. */
export function pointToFraction(point: Point, view: SiteView, fit: Rect): Point {
  const width = fit.width * view.zoom
  const height = fit.height * view.zoom
  if (!(width > 0 && height > 0)) return { x: NaN, y: NaN }
  return { x: (point.x - fit.left - view.x) / width, y: (point.y - fit.top - view.y) / height }
}

/** A pin's fraction as it is saved: inside PIN_MIN..PIN_MAX, 4 decimals (0.16 px on the 1600 px drawing). */
export function clampFraction(value: number): number {
  return Math.round(Math.min(Math.max(value, PIN_MIN), PIN_MAX) * 10_000) / 10_000
}

/** A fraction as a CSS percentage, without the float noise of `0.4 * 100` (40.00000000000001). */
export const asPercent = (fraction: number) => `${Math.round(fraction * 1_000_000) / 10_000}%`

/** The site number a camera's name carries (DLR: "(23) 1869" is site 23): the same digits its pin shows. */
export function siteNumber(name: string): number | null {
  const digits = cameraNumber(name)
  return digits === null ? null : Number(digits)
}

/** By site number; names without a number last, by name. */
export function bySiteNumber<T extends { name: string }>(items: T[]): T[] {
  return items
    .map((item) => ({ item, n: siteNumber(item.name) }))
    .sort((a, b) => {
      if (a.n !== null && b.n !== null && a.n !== b.n) return a.n - b.n
      if (a.n === null && b.n !== null) return 1
      if (a.n !== null && b.n === null) return -1
      return a.item.name.localeCompare(b.item.name)
    })
    .map(({ item }) => item)
}

/** The group's cameras among the map pins (`group_ids`), by site number: the list an API without the site plan's own
 *  `cameras` leaves the site view (amendment 2026-10-18: the drawing's list is the source when present). */
export function groupCameras(pins: CameraMapPin[], groupId: number): CameraMapPin[] {
  return bySiteNumber(pins.filter((p) => p.group_ids?.includes(groupId)))
}
